import { API_BASE } from './base'
import type {
    BatchAddStockItem,
    BatchAddStocksResult,
    ResearchPoolStockListResponse,
    Stock,
    StockPool,
} from '@/types'

async function requestJson<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
    const res = await fetch(input, init)
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
        const detail = (body as { detail?: string }).detail
        throw new Error(detail || `HTTP ${res.status}`)
    }
    return body as T
}

//股票池列表
export async function getPools() {
    const data = await requestJson<unknown>(`${API_BASE}/stock-pools`)
    return Array.isArray(data) ? data : []
}

//创建股票池
export async function createPool(data: {name: string, description: string , is_favorite: boolean}) {
    const res = await fetch(`${API_BASE}/stock-pools`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
    return res.json()
}

//股票列表
export async function getStocks(query: string) {
    const q = query.trim()
    if (!q) return []
    const data = await requestJson<unknown>(
        `${API_BASE}/stocks/search?query=${encodeURIComponent(q)}`,
    )
    return Array.isArray(data) ? data : []
}

/** 查询 ops 研究池股票（批量导入候选） */
export async function getResearchPoolStocks(params: {
    stock_code?: string
    stock_name?: string
    market?: string
    sort_by?: string
    sort_order?: string
    limit?: number
    offset?: number
}): Promise<ResearchPoolStockListResponse> {
    const qs = new URLSearchParams()
    if (params.stock_code) qs.set('stock_code', params.stock_code)
    if (params.stock_name) qs.set('stock_name', params.stock_name)
    if (params.market) qs.set('market', params.market)
    if (params.sort_by) qs.set('sort_by', params.sort_by)
    if (params.sort_order) qs.set('sort_order', params.sort_order)
    qs.set('limit', String(params.limit ?? 100))
    qs.set('offset', String(params.offset ?? 0))
    const data = await requestJson<ResearchPoolStockListResponse>(
        `${API_BASE}/research-pool/stocks?${qs.toString()}`,
    )
    return {
        total: Number(data?.total) || 0,
        items: Array.isArray(data?.items) ? data.items : [],
    }
}

//添加股票到股票池
export async function addStockToPool(data: { pool_id: number } & Stock) {
    const res = await fetch(`${API_BASE}/stock-pools/${data.pool_id}/stocks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    })
    return res.json()
}

/** 批量添加股票到股票池 */
export async function addStocksToPoolBatch(
    poolId: number,
    stocks: BatchAddStockItem[],
): Promise<BatchAddStocksResult> {
    return requestJson<BatchAddStocksResult>(
        `${API_BASE}/stock-pools/${poolId}/stocks/batch`,
        {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ stocks }),
        },
    )
}

//从股票池删除个股
export async function removeStockFromPool(poolId: number, code: string) {
    const res = await fetch(
        `${API_BASE}/stock-pools/${poolId}/stocks/${encodeURIComponent(code)}`,
        { method: 'DELETE' },
    )
    if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || '删除失败')
    }
    return res.json()
}

//更新股票池
export async function updatePool( data: {pool_id: number, is_favorite: boolean}) {
    const res = await fetch(`${API_BASE}/stock-pools/${data.pool_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({is_favorite: data.is_favorite}),
    })
    return res.json()
}

//股票池详情（股票池包含所有股票）
export async function getPoolDetail(poolId: number): Promise<StockPool> {
    const data = await requestJson<StockPool>(`${API_BASE}/stock-pools/${poolId}`)
    return {
        ...data,
        stocks: Array.isArray(data?.stocks) ? data.stocks : [],
    }
}

/** 导出股票池美股在 as_of 决策时点的缺数据矩阵 CSV，返回 Blob 供下载 */
export async function exportPoolMissingFields(
    poolId: number,
    asOf: string,
    limitPerCode = 16,
): Promise<Blob> {
    const qs = new URLSearchParams({
        as_of: asOf,
        limit_per_code: String(limitPerCode),
    })
    const res = await fetch(`${API_BASE}/stock-pools/${poolId}/missing-fields/export?${qs}`)
    if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { detail?: string }).detail || `HTTP ${res.status}`)
    }
    return res.blob()
}

/**
 * 导出股票池全部个股的历史财报宽表 CSV（一行一期、字段各占一列），返回 Blob 供下载。
 *
 * 表头固定前四列：股票代码、报告会计截止日期、报告类型、财报发布日期；其后每个有值字段各占一列。
 */
export async function exportPoolFinancials(
    poolId: number,
    asOf: string,
    limitPerCode = 16,
): Promise<Blob> {
    const qs = new URLSearchParams({
        as_of: asOf,
        limit_per_code: String(limitPerCode),
    })
    const res = await fetch(`${API_BASE}/stock-pools/${poolId}/financials/export?${qs}`)
    if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error((body as { detail?: string }).detail || `HTTP ${res.status}`)
    }
    return res.blob()
}
